import React, { useState, useEffect, useCallback } from "react";
import { 
  FiX, 
  FiCheckCircle, 
  FiArrowRight, 
  FiClock, 
  FiCalendar,
  FiTrendingUp,
  FiUsers,
  FiShield,
  FiZap,
  FiTarget,
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
  FiPlayCircle
} from "react-icons/fi";
import "./NewsDetailsModal.css";

const NewsDetailsModal = ({ post, onClose }) => {
  if (!post) return null;
  return <NewsDetailsModalContent post={post} onClose={onClose} />;
};

export default NewsDetailsModal;

/* ---------------------------------------------------
   CONTENT COMPONENT (ALL HOOKS LIVE HERE)
--------------------------------------------------- */
const NewsDetailsModalContent = ({ post, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  // Feature cards data
  const featureCards = [
    {
      icon: <FiZap />,
      title: "AI-Powered Automation",
      description: "Automate routine HR tasks with intelligent workflows that learn and adapt to your organization's patterns."
    },
    {
      icon: <FiTarget />,
      title: "Real-time Analytics",
      description: "Get instant insights with dashboards that update in real-time as data flows through your HR ecosystem."
    },
    {
      icon: <FiUsers />,
      title: "Employee Self-Service",
      description: "Empower employees with tools to manage their own data, reducing HR administrative workload by 60%."
    },
    {
      icon: <FiShield />,
      title: "Compliance Guard",
      description: "Stay compliant automatically with built-in regulatory updates and audit-ready documentation."
    }
  ];

  // Stats data
  const stats = [
    { value: "85%", label: "Faster Processing" },
    { value: "60%", label: "Cost Reduction" },
    { value: "95%", label: "User Satisfaction" },
    { value: "40%", label: "Time Saved" }
  ];

  // Different images for slider - using actual different images
  const sliderImages = [
    {
      id: 1,
      src: "/images/AI-2.jpg", // Analytics dashboard
      alt: "Dashboard Analytics View",
      title: "Real-time Analytics Dashboard",
      description: "Monitor key HR metrics with interactive visualizations"
    },
    {
      id: 2,
      src: "/images/ai-powerd-readmore-btn.jpg", // Employee interface
      alt: "Employee Management Interface",
      title: "Employee Management Portal",
      description: "Streamlined interface for managing employee data and workflows"
    },
    {
      id: 3,
      src: "/images/incident-management-process-business.avif", // Recruitment
      alt: "Recruitment Pipeline",
      title: "Recruitment Pipeline View",
      description: "Track candidates through every stage of the hiring process"
    },
    {
      id: 4,
      src: "/images/document-management-system.avif", // Compliance
      alt: "Compliance Reporting",
      title: "Compliance & Reporting",
      description: "Automated compliance tracking and report generation"
    }
  ];

  // Auto slide effect
  useEffect(() => {
    let intervalId;
    
    if (isPlaying) {
      intervalId = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
      }, 5000); // Change slide every 5 seconds
    }
    
    // Cleanup function
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPlaying, sliderImages.length]);

  // Navigation functions - using useCallback for better performance
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
  }, [sliderImages.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === 0 ? sliderImages.length - 1 : prev - 1));
  }, [sliderImages.length]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  const togglePlay = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  // Handle overlay click to close modal
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  const handleModalClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <div className="news-modal-overlay" onClick={handleOverlayClick}>
      <div className="news-modal" onClick={handleModalClick}>

        {/* Close Icon */}
        <button className="news-modal-close" onClick={onClose}>
          <FiX />
        </button>

        {/* Hero Image - using post.img as requested */}
        <div className="news-modal-hero">
          <img src={post.img} alt={post.title} />
        </div>

        {/* Content */}
        <div className="news-modal-content">

          {/* Tags */}
          <div className="news-modal-tags">
            {post.category1 && <span>{post.category1}</span>}
            {post.category2 && <span>{post.category2}</span>}
            <span>Feature Update</span>
            <span>HR Technology</span>
          </div>

          <h2 className="news-modal-title">{post.title}</h2>
          
          <p className="news-modal-desc">
            {post.desc} This comprehensive enhancement brings together cutting-edge 
            technology and user-centric design to transform how HR teams operate. 
            By integrating artificial intelligence with intuitive interfaces, 
            we're setting new standards for efficiency and employee engagement.
          </p>

          {/* Divider */}
          <div className="news-divider" />

          {/* Why this matters */}
          <div className="news-modal-section">
            <h4><FiTrendingUp /> Why This Matters</h4>
            <p>
              In today's fast-paced business environment, HR departments face increasing 
              pressure to do more with less. Manual processes and disconnected systems 
              create bottlenecks that slow down operations and impact employee experience. 
              Our solution bridges this gap by providing a unified platform that automates 
              routine tasks while delivering actionable insights for strategic decision-making.
            </p>
          </div>

          {/* Features Grid */}
          <div className="news-features-grid">
            {featureCards.map((feature, index) => (
              <div key={index} className="news-feature-card">
                <div className="news-feature-icon">{feature.icon}</div>
                <h5 className="news-feature-title">{feature.title}</h5>
                <p className="news-feature-desc">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Key Benefits */}
          <div className="news-modal-section">
            <h4>Key Benefits & Impact</h4>
            <ul className="news-benefits">
              <li><FiCheckCircle /> <strong>80% faster</strong> HR operations processing</li>
              <li><FiCheckCircle /> <strong>Reduced manual intervention</strong> by automating repetitive tasks</li>
              <li><FiCheckCircle /> <strong>Improved employee experience</strong> with self-service portals</li>
              <li><FiCheckCircle /> <strong>Scalable workflows</strong> that grow with your organization</li>
              <li><FiCheckCircle /> <strong>Real-time compliance</strong> monitoring and reporting</li>
              <li><FiCheckCircle /> <strong>Enhanced data security</strong> with enterprise-grade encryption</li>
            </ul>
          </div>

          {/* Stats Bar */}
          <div className="news-stats-bar">
            {stats.map((stat, index) => (
              <div key={index} className="news-stat-item">
                <div className="news-stat-value">{stat.value}</div>
                <div className="news-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Image Slider Banner */}
          <div className="news-modal-slider-container">
            <div className="news-slider-header">
              <h4>Feature Gallery</h4>
              <div className="news-slider-controls">
                <button 
                  className="news-slider-play-btn" 
                  onClick={togglePlay}
                  title={isPlaying ? "Pause slideshow" : "Play slideshow"}
                >
                  <FiPlayCircle style={{ transform: isPlaying ? 'none' : 'rotate(90deg)' }} />
                </button>
                <span className="news-slider-count">
                  {currentSlide + 1} / {sliderImages.length}
                </span>
              </div>
            </div>

            <div className="news-slider-wrapper">
              {/* Navigation Buttons */}
              <button 
                className="news-slider-btn news-slider-prev" 
                onClick={prevSlide}
                aria-label="Previous slide"
              >
                <FiChevronLeft />
              </button>
              
              <button 
                className="news-slider-btn news-slider-next" 
                onClick={nextSlide}
                aria-label="Next slide"
              >
                <FiChevronRight />
              </button>

              {/* Slider Images */}
              <div className="news-slider-track">
                {sliderImages.map((image, index) => (
                  <div 
                    key={image.id}
                    className={`news-slider-slide ${index === currentSlide ? 'active' : ''}`}
                  >
                    <div className="news-slider-image-container">
                      <img 
                        src={image.src} 
                        alt={image.alt}
                        className="news-slider-image"
                      />
                      <div className="news-slider-overlay">
                        <div className="news-slider-content">
                          <h5 className="news-slider-title">{image.title}</h5>
                          <p className="news-slider-description">{image.description}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Dots Indicator */}
              <div className="news-slider-dots">
                {sliderImages.map((_, index) => (
                  <button
                    key={index}
                    className={`news-slider-dot ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Thumbnails */}
            <div className="news-slider-thumbnails">
              {sliderImages.map((image, index) => (
                <div 
                  key={image.id}
                  className={`news-slider-thumbnail ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                >
                  <img src={image.src} alt={image.alt} />
                  <div className="news-thumbnail-overlay">
                    <span className="news-thumbnail-number">{index + 1}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Implementation Timeline */}
          <div className="news-modal-section">
            <h4>Implementation Timeline</h4>
            <p>
              Our phased rollout approach ensures minimal disruption to your operations. 
              Most organizations see full implementation within 4-6 weeks, with immediate 
              benefits visible from week one. Our dedicated support team provides 
              comprehensive training and 24/7 assistance throughout the process.
            </p>
          </div>

          {/* Footer Info */}
          <div className="news-modal-footer">
            <div className="news-publish-date">
              <FiCalendar /> Published: {post.date || "March 15, 2024"}
            </div>
            <div className="news-read-time">
              <FiClock /> Read time: 4 min
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};